import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Clock, User, FileSpreadsheet } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_COLORS = {
  brand: 'bg-blue-100 text-blue-800',
  sub_brand: 'bg-indigo-100 text-indigo-800',
  category: 'bg-green-100 text-green-800',
  menu: 'bg-purple-100 text-purple-800',
};

export default function MasterUploadHistory({ open, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) fetchHistory();
  }, [open]);

  async function fetchHistory() {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(API + '/masters/bulk-upload/history', {
        headers: { Authorization: 'Bearer ' + token },
      });
      setHistory(res.data.history || []);
    } catch (err) {
      toast.error('Failed to load upload history');
    } finally {
      setLoading(false);
    }
  }

  function formatDate(d) {
    if (!d) return '-';
    try { return new Date(d).toLocaleString(); } catch { return d; }
  }

  return React.createElement(Dialog, { open: open, onOpenChange: onClose },
    React.createElement(DialogContent, { className: 'max-w-lg' },
      React.createElement(DialogHeader, null,
        React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
          React.createElement(Clock, { className: 'w-5 h-5' }),
          'Master Data Upload History'
        )
      ),
      React.createElement('div', { className: 'max-h-96 overflow-y-auto space-y-2' },
        loading && React.createElement('div', { className: 'text-center py-8 text-gray-500' }, 'Loading...'),
        !loading && history.length === 0 && React.createElement('div', { className: 'text-center py-8 text-gray-500 text-sm' }, 'No upload history yet'),
        history.map(h => React.createElement('div', {
          key: h.id,
          className: 'border rounded-lg p-3 space-y-2',
          'data-testid': 'upload-history-item'
        },
          React.createElement('div', { className: 'flex items-center justify-between' },
            React.createElement(Badge, { className: TYPE_COLORS[h.master_type] || 'bg-gray-100' }, h.master_type_label || h.master_type),
            React.createElement('span', { className: 'text-xs text-gray-500' }, formatDate(h.upload_date))
          ),
          React.createElement('div', { className: 'flex items-center gap-1 text-xs text-gray-500' },
            React.createElement(User, { className: 'w-3 h-3' }),
            h.uploaded_by_name || 'Unknown',
            React.createElement('span', { className: 'mx-1' }, '|'),
            'Mode: ', h.mode === 'update_existing' ? 'Update' : 'Add New'
          ),
          React.createElement('div', { className: 'flex gap-3 text-xs' },
            React.createElement('span', { className: 'text-green-600 font-medium' }, h.added, ' added'),
            React.createElement('span', { className: 'text-blue-600 font-medium' }, h.updated, ' updated'),
            React.createElement('span', { className: 'text-yellow-600 font-medium' }, h.skipped, ' skipped'),
            h.failed > 0 && React.createElement('span', { className: 'text-red-600 font-medium' }, h.failed, ' failed')
          )
        ))
      )
    )
  );
}
